/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { ArrowRight, BarChart3, Compass, Home, Info, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { getEffectiveRef } from "@/lib/refTracker";
import { getJourney } from "@/lib/journeyTracker";

// Sequential journey: OrizzontiPro (1) -> ValutaPro (2) -> PartenzaPro (3)
const PATHS = [
  {
    id: "horizons",
    tool: "orizzonti",
    icon: Compass,
    eyebrow: "1 · Chiarisci la direzione",
    title: "Racconta il progetto che hai in mente",
    text: "Parti dalla tua storia, dai dubbi e dal risultato che vorresti ottenere. Riceverai una prima lettura dello scenario e i passi più coerenti da approfondire.",
    route: "/orizzonti",
    cta: "Inizia da OrizzontiPro",
    ctaResume: "Ripeti OrizzontiPro",
  },
  {
    id: "value",
    tool: "valutapro",
    icon: BarChart3,
    eyebrow: "2 · Comprendi il valore",
    title: "Scopri il potenziale del tuo immobile",
    text: "Analizza caratteristiche, zona e fattori di vendibilità per ottenere una stima indicativa e capire quali elementi incidono davvero.",
    route: "/valuta",
    cta: "Inizia ValutaPro",
    ctaResume: "Continua con ValutaPro",
  },
  {
    id: "prepare",
    tool: "partenza",
    icon: Home,
    eyebrow: "3 · Verifica la preparazione",
    title: "Individua ciò che potrebbe rallentarti",
    text: "Controlla documenti, tempi, criticità e priorità prima che emergano durante una trattativa.",
    route: "/partenza",
    cta: "Inizia PartenzaPro",
    ctaResume: "Continua con PartenzaPro",
  },
];

function buildUrl(route, { ref, email }) {
  const parts = [];
  if (email) parts.push(`email=${encodeURIComponent(email)}`);
  if (ref) parts.push(`ref=${encodeURIComponent(ref)}`);
  return parts.length ? `${route}?${parts.join("&")}` : route;
}

export default function AppEntry() {
  const navigate = useNavigate();
  const ref = getEffectiveRef();
  const journey = getJourney();
  const valutaproModalita = ((journey.tools?.valutapro?.modalita) || "").toLowerCase();
  const isAffittoJourney = valutaproModalita === "affitto";
  // Percorso Affitto: 2 tappe (Orizzonti + ValutaPro Affitto). PartenzaPro non conta.
  // Percorso Vendita/indeciso: 3 tappe standard.
  const basePaths = isAffittoJourney ? PATHS.filter((p) => p.tool !== "partenza") : PATHS;
  // Sovrascrivi copy della card ValutaPro quando il percorso è Affitto.
  const visiblePaths = basePaths.map((p) => {
    if (p.tool !== "valutapro" || !isAffittoJourney) return p;
    return {
      ...p,
      eyebrow: "2 · Comprendi il canone",
      title: "Comprendi il canone",
      text: "Analizza caratteristiche, zona e fattori che incidono sulla locazione per ottenere una stima indicativa del canone.",
      cta: "Inizia ValutaPro",
      ctaResume: "Rivedi ValutaPro",
    };
  });
  const journeyTotal = visiblePaths.length;
  const completedCount = visiblePaths.filter((p) => !!(journey.tools && journey.tools[p.tool])).length;
  const hasJourney = completedCount > 0;
  const journeyCompleted = journeyTotal > 0 && completedCount >= journeyTotal;

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-[#F7F7F7] text-[#0A0A0A]">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-7 sm:py-10">
        <header className="flex items-center justify-between gap-4">
          <Logo size={28} />
          <Link
            to={buildUrl("/scopri", { ref })}
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#5C5C5C] hover:text-[#B40101]"
            data-testid="app-info-link"
          >
            <Info size={17} />
            Come funziona
          </Link>
        </header>

        <section className="max-w-3xl pt-14 sm:pt-20">
          <p
            className="text-[#B40101] font-bold uppercase tracking-[0.16em] text-xs sm:text-sm"
            data-testid="app-eyebrow"
          >
            Prima di decidere, fai chiarezza
          </p>
          <h1
            className="font-display text-4xl sm:text-6xl font-bold leading-[1.03] mt-4"
            data-testid="app-title"
          >
            {isAffittoJourney
              ? "Due passi per capire il canone realistico del tuo immobile."
              : "Tre passi per capire quale futuro dare al tuo immobile."}
          </h1>
          <p className="text-lg sm:text-xl text-[#666] mt-5 max-w-2xl leading-relaxed" data-testid="app-intro">
            {isAffittoJourney
              ? "ValutaGenio ti aiuta a mettere ordine prima di affittare la tua proprietà. Parti dalla tua situazione e comprendi il canone realistico del tuo immobile."
              : "ValutaGenio ti aiuta a mettere ordine prima di vendere, affittare o investire altro denaro nella proprietà. Parti dalla tua situazione, comprendi il valore dell'immobile e verifica quanto sei pronto a procedere."}
          </p>
          <p className="text-sm text-[#666] mt-4 max-w-2xl leading-relaxed" data-testid="app-reassure">
            Le analisi sono gratuite. Non richiedono registrazione e non comportano alcun incarico.
          </p>
        </section>

        <section className="pt-14 sm:pt-16" aria-labelledby="app-percorso-title">
          <div className="flex flex-wrap items-baseline gap-3">
            <h2
              id="app-percorso-title"
              className="font-display text-2xl sm:text-3xl font-bold text-[#0A0A0A]"
              data-testid="app-percorso-title"
            >
              {isAffittoJourney ? "Il percorso Affitto" : "Il percorso consigliato"}
            </h2>
            {hasJourney && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full bg-[#B40101]/10 text-[#B40101] uppercase tracking-widest"
                data-testid="app-journey-progress-badge"
              >
                Percorso {completedCount}/{journeyTotal}
              </span>
            )}
          </div>
          <p className="text-[#666] mt-3 max-w-2xl leading-relaxed" data-testid="app-percorso-intro">
            {isAffittoJourney
              ? "Il percorso Affitto si conclude con la stima del canone. Se vuoi passare a un'analisi Vendita, apri ValutaPro e cambia modalità."
              : "Ogni passaggio risponde a una domanda diversa. Puoi seguirli in ordine oppure scegliere soltanto ciò che ti serve oggi."}
          </p>
        </section>

        <section className="grid lg:grid-cols-3 gap-5 mt-8" aria-label="Percorsi ValutaGenio">
          {visiblePaths.map((path, idx) => {
            const Icon = path.icon;
            const done = !!(journey.tools && journey.tools[path.tool]);
            const target = buildUrl(path.route, { ref, email: journey.email });
            const label = done ? path.ctaResume : path.cta;
            return (
              <button
                key={path.id}
                type="button"
                onClick={() => navigate(target)}
                className={`group text-left bg-white border rounded-2xl p-6 sm:p-7 shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.10)] transition-all ${done ? "border-[#B40101]/50 bg-[#FFF7F7]" : "border-[#E5E5E5] hover:border-[#B40101]/40"}`}
                data-testid={`app-path-${path.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="w-11 h-11 rounded-xl bg-[#B40101]/8 text-[#B40101] flex items-center justify-center">
                    <Icon size={23} />
                  </span>
                  {done ? (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#10B981]/12 text-[#065F46] uppercase tracking-widest"
                      data-testid={`app-path-status-${path.id}`}
                    >
                      <Check size={12} /> Completato
                    </span>
                  ) : hasJourney && idx === completedCount ? (
                    <span
                      className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#B40101]/10 text-[#B40101] uppercase tracking-widest"
                      data-testid={`app-path-status-${path.id}`}
                    >
                      Prossimo passo
                    </span>
                  ) : null}
                </div>
                <span className="block text-xs font-bold uppercase tracking-[0.12em] text-[#B40101] mt-6">
                  {path.eyebrow}
                </span>
                <span className="block font-display text-2xl font-bold leading-tight mt-3">
                  {path.title}
                </span>
                <span className="block text-[#6B6B6B] leading-relaxed mt-4">
                  {path.text}
                </span>
                <span className="inline-flex items-center gap-2 text-[#B40101] font-bold mt-7">
                  {label}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            );
          })}
        </section>

        {isAffittoJourney && journeyCompleted && (
          <p className="text-center text-sm text-[#0A0A0A] mt-8 max-w-3xl mx-auto leading-relaxed" data-testid="app-affitto-completed-note">
            Hai completato il percorso Affitto. Se vuoi, puoi ora confrontarti con un Property Advisor.
          </p>
        )}

        <p className="text-center text-sm text-[#666] mt-12 max-w-3xl mx-auto leading-relaxed" data-testid="app-closure">
          Puoi fermarti dopo una sola analisi. Ogni passaggio completato resta salvato nel tuo percorso.
        </p>
      </div>
    </main>
  );
}
